import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@d-environments/environment';
import { IDialogCoreResponse } from '@d-models/other/user.model';
import { CoreUserService } from '@d-services/core/user.service';
import { AuthService } from '@d-services/wms/auth.service';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { ModalService } from '../other/modal.service';
import { SSOService } from '../other/sso.service';

@Injectable()
export class CoreHttpService {
  private BASE_URL = environment.coreApp.url;
  constructor(
    private http: HttpClient,
    private modalService: ModalService,
    private authService: AuthService,
    private coreUserService: CoreUserService,
    private ssoService: SSOService,
  ) {}

  buildHeaders(
    headers: any = {},
    {
      useTenantCompanyId = false,
      useUserInfoCompanyId = true,
    }: CoreHttpOptions = {},
  ) {
    const defaultHeaders: any = {
      Authorization: this.authService.getAuthorizationToken() ?? '',
      TenantId:
        this.coreUserService.getUserLevel().tenantId ||
        this.ssoService.getTenantId(),
    };
    // Don't automatically add CompanyId if MerchantId or CompanyId is specified
    if (!headers.MerchantId && !headers.CompanyId) {
      // If user is at PO level, use PO's company id
      const userLevel = this.coreUserService.getUserLevel();
      if (useUserInfoCompanyId) {
        if (userLevel.level > 1) {
          defaultHeaders.CompanyId = userLevel.companyId;
        } else if (useTenantCompanyId) {
          defaultHeaders.CompanyId =
            this.coreUserService.getCurrentTenant()?.tenantCompanyId;
        }
      }
    }
    // Omit empty headers
    return Object.fromEntries(
      Object.entries<string>({ ...headers, ...defaultHeaders }).filter(
        ([_, v]) => v,
      ),
    );
  }

  private withInterceptor<T>(obs: Observable<T>, { hideLoading = false } = {}) {
    if (!hideLoading) this.modalService.showLoading();
    return obs.pipe(
      tap<T>(() => !hideLoading && this.modalService.hideLoading()),
      catchError((e) => {
        if (!hideLoading) this.modalService.hideLoading();
        return throwError(() => (e?.status ? new HttpErrorResponse(e) : e));
      }),
    );
  }

  post<ResponseBody = any, Body = any>(
    url: string,
    body: Body,
    options: CoreHttpOptions = {},
  ) {
    return this.withInterceptor(
      this.http.post<IDialogCoreResponse<ResponseBody>>(
        `${this.BASE_URL}/${url}`,
        body,
        {
          headers: this.buildHeaders(options.headers, options),
        },
      ),
      options,
    );
  }

  put<ResponseBody = any, Body = any>(
    url: string,
    body: Body,
    options: CoreHttpOptions = {},
  ) {
    return this.withInterceptor(
      this.http.put<IDialogCoreResponse<ResponseBody>>(
        `${this.BASE_URL}/${url}`,
        body,
        {
          headers: this.buildHeaders(options.headers, options),
        },
      ),
      options,
    );
  }

  get<ResponseBody = any>(url: string, options: CoreHttpOptions = {}) {
    return this.withInterceptor(
      this.http.get<IDialogCoreResponse<ResponseBody>>(
        `${this.BASE_URL}/${url}`,
        {
          headers: this.buildHeaders(options.headers, options),
        },
      ),
      options,
    );
  }
}

interface CoreHttpOptions {
  headers?: any;
  hideLoading?: boolean;
  useTenantCompanyId?: boolean;
  useUserInfoCompanyId?: boolean;
}
